import { useEffect, useMemo, useState } from 'react';
import { useGetUserTasks } from '@/hooks/queries/tasks';
import { useGetAllGroups } from '@/hooks/queries/groups';
import { useGetCurrentUserDetails } from '@/hooks/queries/user';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock3 } from 'lucide-react';
import type { UserTaskListItem } from '@/types';
import { NotificationsPagination } from '@/components/notifications/NotificationsUI';
import { useNavigate } from '@tanstack/react-router';
import { StatusBadge } from '@/components/task/TaskOverview/StatusBadge';
import { formatDuration } from '@/lib/task-time';

type StatusFilter = 'ALL' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
type OwnershipFilter = 'ASSIGNED_TO_ME' | 'CREATED_BY_ME';

/**
 * Display name for an assignee on a tasks-list row.
 */
function formatUserTaskListAssigneeName(assignedUser: UserTaskListItem['assignedUser']): string | undefined {
  if (!assignedUser) return undefined;
  const name = `${assignedUser.firstName ?? ''} ${assignedUser.lastName ?? ''}`.trim();
  return name || undefined;
}

/**
 * Live elapsed timer pill for task rows.
 */
function TaskRowTimer({ createdAt }: TaskRowTimerProps) {
  const [timerNow, setTimerNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => {
      setTimerNow(Date.now());
    }, 1000);

    return () => window.clearInterval(interval);
  }, []);

  const elapsedMs = Math.max(timerNow - createdAt, 0);

  return (
    <div
      className="inline-flex h-6 min-w-[74px] items-center justify-center gap-2 rounded-2xl border border-[#D9DDE5] bg-[#F2F4F8] px-3"
      title="Elapsed time since task creation"
    >
      <Clock3 className="h-4 w-4 text-[#8DA1C1]" />
      <span className="text-sm font-semibold leading-none tracking-wide text-[#334665]">
        {formatDuration(elapsedMs)}
      </span>
    </div>
  );
}

function TasksPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [ownership, setOwnership] = useState<OwnershipFilter>('ASSIGNED_TO_ME');
  const [groupId, setGroupId] = useState<string>('all');

  const { data: groupsData } = useGetAllGroups();
  const { data: currentUserDetailsData } = useGetCurrentUserDetails();
  const groups = groupsData?.data || [];
  const currentUserId = currentUserDetailsData?.data?.id;

  const groupNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) {
      map.set(String(g.id), g.name);
    }
    return map;
  }, [groups]);

  const queryParams = useMemo(() => {
    return {
      status: status === 'ALL' ? undefined : (status as Exclude<StatusFilter, 'ALL'>),
      groupId: groupId === 'all' ? undefined : groupId,
    };
  }, [status, groupId]);

  const { data, isPending, isFetching } = useGetUserTasks(queryParams);
  const tasks = useMemo(() => {
    const userTasks = data?.data || [];
    if (!currentUserId) return userTasks;

    return userTasks.filter((task) => {
      if (ownership === 'ASSIGNED_TO_ME') {
        return task.assignedUser?.id === currentUserId;
      }

      return task.user?.id === currentUserId;
    });
  }, [currentUserId, data?.data, ownership]);

  // Client-side pagination (API does not define paging params here)
  const [pageNo, setPageNo] = useState<number>(1);
  const pageSize = 10;
  const totalItems = tasks.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIdx = (pageNo - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalItems);
  const pageItems = tasks.slice(startIdx, endIdx);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setPageNo(page);
  };

  // Reset to page 1 when filters change
  useEffect(() => { setPageNo(1); }, [status, groupId, ownership]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-6 p-3">
      <div className="flex shrink-0 flex-col items-start gap-2">
        {/* <div className="flex items-center gap-2 text-foreground">
          <ListChecks className="h-5 w-5" />
          <h1 className="text-xl font-semibold">Tasks</h1>
        </div> */}
        <div className="flex w-full items-center gap-2">
          <Select value={ownership} onValueChange={(v: OwnershipFilter) => setOwnership(v)}>
            <SelectTrigger className="min-w-36">
              <SelectValue placeholder="Task Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ASSIGNED_TO_ME">Assigned to me</SelectItem>
              <SelectItem value="CREATED_BY_ME">Created by me</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2">
          <Select value={status} onValueChange={(v: StatusFilter) => setStatus(v)}>
            <SelectTrigger className="min-w-24">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="FAILED">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={groupId} onValueChange={setGroupId}>
            <SelectTrigger className="min-w-32">
              <SelectValue placeholder="All Groups" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Groups</SelectItem>
              {groups.map((g) => (
                <SelectItem key={g.id} value={String(g.id)}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>


      <NotificationsPagination
        pageNo={pageNo}
        pageSize={pageSize}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        disablePagination={isPending || isFetching}
      />

      <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto">
        {isPending ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between gap-4 rounded-lg border bg-(--color-surface-light-sl-1) p-4">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
                <div className="flex shrink-0 items-center gap-4 text-muted-foreground">
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-6 w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : tasks.length === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">No Tasks</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground text-sm">
              No tasks found for the selected filters.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {pageItems.map((row: UserTaskListItem) => {
              const assignedName = formatUserTaskListAssigneeName(row.assignedUser);
              const groupLabel = groupNameById.get(String(row.groupId));
              return (
                <div
                  key={String(row.listItemId)}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    navigate({
                      to: '/group/$groupId/item/$folderId',
                      params: { groupId: String(row.groupId), folderId: String(row.listItemId) },
                    })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate({
                        to: '/group/$groupId/item/$folderId',
                        params: { groupId: String(row.groupId), folderId: String(row.listItemId) },
                      });
                    }
                  }}
                  className="flex items-center justify-between gap-4 rounded-lg border bg-[var(--color-surface-light-sl-1)] p-4 transition-shadow hover:shadow cursor-pointer"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 justify-between">
                      <div className='flex items-center gap-1'>
                        <div className="text-sm font-medium line-clamp-1">
                          {row.title?.trim() || 'Untitled Task'}
                        </div>
                        <StatusBadge statusLabel={row.status} className="text-xs" />
                      </div>
                      {/* <TaskRowTimer createdAt={row.createdAt} /> */}
                    </div>
                    <div className="mt-1 flex items-center gap-4 text-sm text-muted-foreground justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="line-clamp-1 text-xs underline">
                          {assignedName ? (
                            <span className="inline-flex text-xs underline items-center gap-2">
                              {assignedName}
                            </span>
                          ) : (
                            'Unassigned'
                          )}
                        </span>
                      </div>
                      {/* <div className="flex items-center text-xs gap-2 shrink-0">
                        <Calendar className="h-4 w-4 text-xs" />
                        <span>Created: {new Date(row.createdAt).toLocaleDateString()}</span>
                      </div> */}
                      {groupLabel ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className="inline-flex shrink-0"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <Badge variant="outline" className="text-xs">{groupLabel}</Badge>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" sideOffset={4}>
                            Group: {groupLabel}
                          </TooltipContent>
                        </Tooltip>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default TasksPage;

type TaskRowTimerProps = {
  createdAt: number;
};
